<template>
  <section class="h-full overflow-y-auto bg-gray-50">
    <div class="mx-auto w-full max-w-[1500px] px-3 py-4 sm:px-5 lg:px-7">
      <header class="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 class="text-xl font-semibold text-gray-900 sm:text-2xl">{{ labels.title }}</h1>
          <p class="mt-1 text-sm text-gray-500">{{ holidays.length }} {{ labels.daysForYear }} {{ selectedYear }}</p>
        </div>

        <div class="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <button type="button" :disabled="loading || selectedYear <= minYear" @click="changeYear(-1)"
            class="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:border-orange-400 hover:text-orange-600 disabled:cursor-not-allowed disabled:opacity-40"
            :aria-label="labels.previousYear" :title="labels.previousYear">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div class="flex h-10 min-w-[7rem] items-center justify-center border-y border-gray-300 bg-white px-4 text-lg font-semibold text-gray-900">
            {{ selectedYear }}
          </div>
          <button type="button" :disabled="loading || selectedYear >= maxYear" @click="changeYear(1)"
            class="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:border-orange-400 hover:text-orange-600 disabled:cursor-not-allowed disabled:opacity-40"
            :aria-label="labels.nextYear" :title="labels.nextYear">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
          <button v-if="selectedYear !== currentYear" type="button" :disabled="loading" @click="goToCurrentYear"
            class="h-10 rounded-md border border-orange-500 bg-white px-3 text-sm font-semibold text-orange-600 transition hover:bg-orange-50 disabled:opacity-40">
            {{ labels.today }}
          </button>
        </div>
      </header>

      <div class="mb-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-600 sm:text-sm">
        <div class="flex items-center gap-2"><span class="h-3 w-3 rounded-sm bg-orange-400"></span>{{ labels.publicHoliday }}</div>
        <div class="flex items-center gap-2"><span class="h-3 w-3 rounded-sm bg-blue-500"></span>{{ labels.compensation }}</div>
        <div class="flex items-center gap-2"><span class="h-3 w-3 rounded-sm bg-emerald-500"></span>{{ labels.exceptional }}</div>
        <div class="flex items-center gap-2"><span class="h-3 w-3 rounded-sm bg-gray-300"></span>{{ labels.sunday }}</div>
      </div>

      <div v-if="loading" class="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3" aria-live="polite">
        <div v-for="month in 12" :key="month" class="h-[27rem] animate-pulse rounded-md border border-gray-200 bg-white p-4">
          <div class="mx-auto h-5 w-28 rounded bg-gray-200"></div>
          <div class="mt-5 grid grid-cols-7 gap-1">
            <div v-for="cell in 42" :key="cell" class="h-9 rounded bg-gray-100"></div>
          </div>
        </div>
      </div>

      <div v-else-if="errorMessage" class="flex min-h-[18rem] flex-col items-center justify-center text-center">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
        </div>
        <p class="mt-3 text-sm text-gray-600">{{ errorMessage }}</p>
        <button type="button" @click="loadHolidays"
          class="mt-4 rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">
          {{ labels.retry }}
        </button>
      </div>

      <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
        <article v-for="month in months" :key="month.index" class="rounded-md border border-gray-200 bg-white shadow-sm">
          <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h2 class="font-semibold capitalize text-gray-900">{{ month.name }}</h2>
            <span v-if="month.holidays.length" class="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
              {{ month.holidays.length }}
            </span>
          </div>

          <div class="p-3 sm:p-4">
            <div class="grid grid-cols-7 gap-1 text-center text-[0.68rem] font-semibold uppercase text-gray-400">
              <div v-for="weekday in weekdays" :key="weekday" class="py-1">{{ weekday }}</div>
            </div>
            <div class="mt-1 grid grid-cols-7 gap-1">
              <div v-for="cell in month.cells" :key="cell.key"
                class="relative flex h-9 items-center justify-center rounded-md text-sm sm:h-10"
                :class="calendarCellClasses(cell)" :title="cell.holiday ? localizedHolidayName(cell.holiday) : ''">
                <span v-if="cell.day">{{ cell.day }}</span>
                <span v-if="cell.holiday" class="absolute bottom-1 h-1 w-1 rounded-full" :class="holidayDotClass(cell.holiday.type)"></span>
              </div>
            </div>

            <div class="mt-4 min-h-[4.25rem] border-t border-gray-100 pt-3">
              <div v-if="month.holidays.length" class="space-y-2">
                <div v-for="holiday in month.holidays" :key="holiday.date" class="flex items-start gap-2 text-xs sm:text-sm">
                  <span class="mt-1.5 h-2 w-2 flex-shrink-0 rounded-sm" :class="holidayDotClass(holiday.type)"></span>
                  <div class="min-w-0">
                    <span class="font-semibold text-gray-800">{{ formatHolidayDate(holiday.date) }}</span>
                    <span class="text-gray-600"> · {{ localizedHolidayName(holiday) }}</span>
                  </div>
                </div>
              </div>
              <p v-else class="text-xs text-gray-400">{{ labels.noHoliday }}</p>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
const holidayTranslations = {
  "Jour de l'An": "New Year's Day",
  'Fête de la Jeunesse': 'Youth Day',
  'Fête du Travail': 'Labour Day',
  'Fête Nationale (Unification)': 'National Day (Unification)',
  'Assomption': 'Assumption Day',
  'Noël': 'Christmas Day',
  'Vendredi Saint': 'Good Friday',
  'Ascension': 'Ascension Day',
  'Fête de fin du Ramadan': 'Eid al-Fitr',
  'Fête du sacrifice': 'Eid al-Adha'
}

export default {
  name: 'JoursFeriesComponent',
  data() {
    const currentYear = this.doualaCurrentYear()

    return {
      minYear: 2000,
      maxYear: 2100,
      currentYear,
      selectedYear: currentYear,
      holidays: [],
      loading: true,
      errorMessage: ''
    }
  },
  computed: {
    language() {
      return this.$i18n.locale === 'en' ? 'en' : 'fr'
    },
    labels() {
      return this.language === 'en'
        ? {
            title: 'Public holiday calendar', daysForYear: 'public holidays in', previousYear: 'Previous year',
            nextYear: 'Next year', today: 'Current year', publicHoliday: 'Public holiday', compensation: 'Monday compensation',
            exceptional: 'Exceptional public holiday', sunday: 'Sunday (closed)', retry: 'Try again', noHoliday: 'No public holiday'
          }
        : {
            title: 'Calendrier des jours fériés', daysForYear: 'jours fériés en', previousYear: 'Année précédente',
            nextYear: 'Année suivante', today: 'Année actuelle', publicHoliday: 'Jour férié', compensation: 'Report au lundi',
            exceptional: 'Jour exceptionnel confirmé', sunday: 'Dimanche (fermé)', retry: 'Réessayer', noHoliday: 'Aucun jour férié'
          }
    },
    weekdays() {
      return this.language === 'en' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] : ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
    },
    months() {
      const formatter = new Intl.DateTimeFormat(this.language === 'en' ? 'en-US' : 'fr-FR', {
        month: 'long',
        timeZone: 'UTC'
      })

      return Array.from({ length: 12 }, (_, monthIndex) => {
        const holidays = this.holidays.filter((holiday) => Number(holiday.date.slice(5, 7)) === monthIndex + 1)
        const holidayMap = Object.fromEntries(holidays.map((holiday) => [holiday.date, holiday]))
        const firstDay = new Date(Date.UTC(this.selectedYear, monthIndex, 1))
        const daysInMonth = new Date(Date.UTC(this.selectedYear, monthIndex + 1, 0)).getUTCDate()
        const offset = (firstDay.getUTCDay() + 6) % 7

        const cells = Array.from({ length: 42 }, (_, cellIndex) => {
          const day = cellIndex - offset + 1

          if (day < 1 || day > daysInMonth) {
            return { key: `empty-${monthIndex}-${cellIndex}`, day: null, holiday: null, isSunday: false, isToday: false }
          }

          const date = `${this.selectedYear}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
          const weekDay = new Date(Date.UTC(this.selectedYear, monthIndex, day)).getUTCDay()

          return {
            key: date,
            day,
            holiday: holidayMap[date] || null,
            isSunday: weekDay === 0,
            isToday: date === this.doualaTodayDate()
          }
        })

        return {
          index: monthIndex,
          name: formatter.format(new Date(Date.UTC(this.selectedYear, monthIndex, 1))),
          holidays,
          cells
        }
      })
    }
  },
  mounted() {
    this.loadHolidays()
  },
  methods: {
    doualaDateParts() {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Africa/Douala',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(new Date())

      return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]))
    },
    doualaCurrentYear() {
      return Number(this.doualaDateParts().year)
    },
    doualaTodayDate() {
      const parts = this.doualaDateParts()
      return `${parts.year}-${parts.month}-${parts.day}`
    },
    loadHolidays() {
      this.loading = true
      this.errorMessage = ''

      this.axios.get(`${this.$store.state.api}cameroon-holidays/${this.selectedYear}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwtToken')}`
        }
      }).then(({ data }) => {
        this.holidays = Array.isArray(data?.holidays) ? data.holidays : []
      }).catch(() => {
        this.errorMessage = this.language === 'en'
          ? 'Unable to load the public holiday calendar.'
          : 'Impossible de charger le calendrier des jours fériés.'
      }).finally(() => {
        this.loading = false
      })
    },
    changeYear(offset) {
      const nextYear = this.selectedYear + offset
      if (nextYear < this.minYear || nextYear > this.maxYear) return
      this.selectedYear = nextYear
      this.loadHolidays()
    },
    goToCurrentYear() {
      this.selectedYear = this.currentYear
      this.loadHolidays()
    },
    localizedHolidayName(holiday) {
      if (this.language === 'fr') return holiday.name

      if (holiday.name_en) return holiday.name_en

      if (holiday.name.startsWith('Report de ')) {
        const originalName = holiday.name.slice('Report de '.length)
        return `Observed ${holidayTranslations[originalName] || originalName}`
      }

      return holidayTranslations[holiday.name] || holiday.name
    },
    formatHolidayDate(dateString) {
      const [year, month, day] = dateString.split('-').map(Number)
      return new Intl.DateTimeFormat(this.language === 'en' ? 'en-US' : 'fr-FR', {
        day: '2-digit',
        month: 'short',
        timeZone: 'UTC'
      }).format(new Date(Date.UTC(year, month - 1, day)))
    },
    holidayDotClass(type) {
      if (type === 'compensation') return 'bg-blue-500'
      if (type === 'exceptional') return 'bg-emerald-500'
      return 'bg-orange-400'
    },
    calendarCellClasses(cell) {
      if (!cell.day) return 'text-transparent'

      const classes = []
      if (cell.isSunday && !cell.holiday) classes.push('bg-gray-100 text-gray-500')
      if (!cell.isSunday && !cell.holiday) classes.push('text-gray-700')
      if (cell.holiday?.type === 'compensation') classes.push('bg-blue-100 font-semibold text-blue-800 ring-1 ring-inset ring-blue-300')
      else if (cell.holiday?.type === 'exceptional') classes.push('bg-emerald-100 font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-300')
      else if (cell.holiday) classes.push('bg-orange-100 font-semibold text-orange-800 ring-1 ring-inset ring-orange-300')
      if (cell.isToday) classes.push('outline outline-2 outline-offset-1 outline-gray-900')

      return classes.join(' ')
    }
  }
}
</script>
